import { useState } from 'react';
import type { FormEvent } from 'react';
import type { ReferenceCreateBody, ReferenceListItem } from '@cockpit/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { parseEuroToCentimes, parsePctToCentiemes } from '../../lib/format';

interface Props {
  initial?: ReferenceListItem | null;
  submitting?: boolean;
  submitLabel?: string;
  onSubmit: (body: ReferenceCreateBody) => void;
  onCancel: () => void;
}

function eurosFromCentimes(c: number): string {
  return (c / 100).toString().replace('.', ',');
}

function m2FromCentiM2(c: number): string {
  return (c / 100).toString().replace('.', ',');
}

function pctFromCentiemes(c: number | null): string {
  return c === null ? '' : (c / 100).toString().replace('.', ',');
}

export function ReferenceForm({
  initial,
  submitting,
  submitLabel = 'Créer la référence',
  onSubmit,
  onCancel,
}: Props): JSX.Element {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [prixPro, setPrixPro] = useState(
    initial !== null && initial !== undefined
      ? eurosFromCentimes(initial.prix_pro_centimes_par_m2)
      : '',
  );
  const [prixRev, setPrixRev] = useState(
    initial !== null && initial !== undefined
      ? eurosFromCentimes(initial.prix_revendeur_centimes_par_m2)
      : '',
  );
  const [prixPart, setPrixPart] = useState(
    initial !== null && initial !== undefined
      ? eurosFromCentimes(initial.prix_particulier_centimes_par_m2)
      : '',
  );
  const [seuil, setSeuil] = useState(
    initial !== null && initial !== undefined ? m2FromCentiM2(initial.seuil_alerte_centi_m2) : '',
  );
  const [margePro, setMargePro] = useState(pctFromCentiemes(initial?.marge_cible_pro_pct ?? null));
  const [margeRev, setMargeRev] = useState(
    pctFromCentiemes(initial?.marge_cible_revendeur_pct ?? null),
  );
  const [margePart, setMargePart] = useState(
    pctFromCentiemes(initial?.marge_cible_particulier_pct ?? null),
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    const pp = parseEuroToCentimes(prixPro);
    const pr = parseEuroToCentimes(prixRev);
    const pc = parseEuroToCentimes(prixPart);
    if (pp === null || pr === null || pc === null) {
      setError('Prix invalides (3 prix obligatoires).');
      return;
    }
    const s = seuil.trim() === '' ? 0 : (parseEuroToCentimes(seuil) ?? 0);
    onSubmit({
      nom: nom.trim(),
      code: code.trim() === '' ? null : code.trim(),
      description: description.trim() === '' ? null : description.trim(),
      prix_pro_centimes_par_m2: pp,
      prix_revendeur_centimes_par_m2: pr,
      prix_particulier_centimes_par_m2: pc,
      seuil_alerte_centi_m2: s,
      marge_cible_pro_pct: margePro.trim() === '' ? null : parsePctToCentiemes(margePro),
      marge_cible_revendeur_pct: margeRev.trim() === '' ? null : parsePctToCentiemes(margeRev),
      marge_cible_particulier_pct: margePart.trim() === '' ? null : parsePctToCentiemes(margePart),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error !== null && (
        <div className="text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Nom *"
          value={nom}
          onChange={(e): void => setNom(e.target.value)}
          required
          className="md:col-span-2"
        />
        <Input label="Code" value={code} onChange={(e): void => setCode(e.target.value)} />
        <Input
          label="Description"
          value={description}
          onChange={(e): void => setDescription(e.target.value)}
          className="md:col-span-3"
        />
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Prix pro €/m² *"
          value={prixPro}
          onChange={(e): void => setPrixPro(e.target.value)}
          required
        />
        <Input
          label="Prix revendeur €/m² *"
          value={prixRev}
          onChange={(e): void => setPrixRev(e.target.value)}
          required
        />
        <Input
          label="Prix particulier €/m² *"
          value={prixPart}
          onChange={(e): void => setPrixPart(e.target.value)}
          required
        />
        <Input
          label="Seuil alerte (m²)"
          value={seuil}
          onChange={(e): void => setSeuil(e.target.value)}
          hint="0 = pas d'alerte"
        />
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Marge cible pro (%)"
          value={margePro}
          onChange={(e): void => setMargePro(e.target.value)}
        />
        <Input
          label="Marge cible revendeur (%)"
          value={margeRev}
          onChange={(e): void => setMargeRev(e.target.value)}
        />
        <Input
          label="Marge cible particulier (%)"
          value={margePart}
          onChange={(e): void => setMargePart(e.target.value)}
        />
      </section>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting === true ? 'Enregistrement…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
